import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  Fab,
  FormControlLabel,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DrawIcon from '@mui/icons-material/Draw';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { Helmet } from 'react-helmet-async';

import config from '@src/config';
import leadGurusLogo from '@src/assets/logo-full-dark.png';
import { toast } from '@src/components/core/toaster';
import { fetchContractForSigning, signContract } from '@src/api/actions/contracts';

const FIELD_CONFIG = {
  signature: { label: 'Sign Here', color: '#ff680d', required: true, type: 'signature' },
  printed_name: { label: 'Full Name', color: '#ff680d', required: true, type: 'text' },
  title: { label: 'Title', color: '#2563eb', required: false, type: 'text' },
  date: { label: 'Date', color: '#2563eb', required: false, type: 'auto' },
  billing_option_1: { label: 'Select Option 1', color: '#f59e0b', required: true, type: 'checkbox', group: 'billing_option' },
  billing_option_2: { label: 'Select Option 2', color: '#f59e0b', required: true, type: 'checkbox', group: 'billing_option' },
  initials: { label: 'Initials', color: '#f59e0b', required: true, type: 'text' },
};

export default function ContractSignPage() {
  const { signToken } = useParams();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const contractRef = useRef(null);

  const [fields, setFields] = useState({
    printed_name: '', signature: '', title: '',
    billing_option_1: false, billing_option_2: false,
    initials: '',
  });
  const [signatureImage, setSignatureImage] = useState('');
  const [signatureType, setSignatureType] = useState('typed');
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [signed, setSigned] = useState(false);

  // Tour
  const [tourActive, setTourActive] = useState(true);
  const [tourStep, setTourStep] = useState(-1);
  const [fieldList, setFieldList] = useState([]); // [{fieldKey, baseKey, nodeIndex}]

  // Modals
  const [activeField, setActiveField] = useState(null);
  const [sigModalOpen, setSigModalOpen] = useState(false);
  const [sigModalTab, setSigModalTab] = useState(0);
  const [sigModalTyped, setSigModalTyped] = useState('');
  const canvasRef = useRef(null);
  const isDrawingRef = useRef(false);

  // Use refs so event handlers always see latest state
  const fieldsRef = useRef(fields);
  const tourActiveRef = useRef(tourActive);
  const tourStepRef = useRef(tourStep);
  const fieldListRef = useRef(fieldList);
  fieldsRef.current = fields;
  tourActiveRef.current = tourActive;
  tourStepRef.current = tourStep;
  fieldListRef.current = fieldList;

  useEffect(() => {
    fetchContractForSigning(signToken)
      .then((res) => setContract(res?.data))
      .catch((err) => setError(err?.response?.data?.detail || 'Unable to load contract.'))
      .finally(() => setLoading(false));
  }, [signToken]);

  // After HTML renders, find field nodes and style them
  useEffect(() => {
    if (!contract?.html || !contractRef.current) return;
    const el = contractRef.current;
    const list = [];
    const seen = {};

    el.querySelectorAll('[data-field]').forEach((node, idx) => {
      let fieldKey = node.getAttribute('data-field');
      if (seen[fieldKey]) {
        fieldKey = fieldKey + '_dup_' + idx;
      }
      seen[fieldKey] = true;
      const baseKey = node.getAttribute('data-field');
      node.setAttribute('data-field-index', String(idx));
      list.push({ fieldKey, baseKey, nodeIndex: idx });

      // Style
      const cfg = FIELD_CONFIG[fieldKey] || FIELD_CONFIG[baseKey];
      if (!cfg || cfg.type === 'auto') {
        // Auto-fill date
        if (baseKey === 'date') {
          node.textContent = new Date().toLocaleDateString('en-US');
          node.style.borderBottom = '2px solid #16a34a';
          node.style.backgroundColor = '#16a34a10';
        }
        return;
      }

      node.style.cursor = 'pointer';
      node.style.transition = 'all 0.2s';
      if (cfg.type === 'checkbox') {
        Object.assign(node.style, {
          border: `2px solid ${cfg.color}`,
          backgroundColor: `${cfg.color}15`,
          borderRadius: '3px',
        });
      } else {
        Object.assign(node.style, {
          borderBottom: `2px dashed ${cfg.color}`,
          backgroundColor: `${cfg.color}12`,
          padding: '4px 10px',
          borderRadius: '4px',
          display: 'inline-block',
          minWidth: '120px',
        });
      }
    });

    setFieldList(list);
    updateDisplays(el, list);
  }, [contract?.html]);

  // Single delegated click handler on the contract container
  const handleContractClick = useCallback((e) => {
    const target = e.target.closest('[data-field]');
    if (!target) return;
    e.preventDefault();
    e.stopPropagation();

    const idx = parseInt(target.getAttribute('data-field-index'), 10);
    const entry = fieldListRef.current.find((f) => f.nodeIndex === idx);
    if (!entry) return;

    const baseKey = entry.baseKey;
    const cfg = FIELD_CONFIG[entry.fieldKey] || FIELD_CONFIG[baseKey];
    if (!cfg || cfg.type === 'auto') return;

    const resolvedKey = entry.fieldKey.startsWith('initials_dup_') ? 'initials' : entry.fieldKey;

    if (resolvedKey === 'signature') {
      setSigModalOpen(true);
    } else if (cfg.type === 'checkbox') {
      setFields((prev) => {
        if (resolvedKey === 'billing_option_1') return { ...prev, billing_option_1: true, billing_option_2: false };
        if (resolvedKey === 'billing_option_2') return { ...prev, billing_option_2: true, billing_option_1: false };
        return prev;
      });
      if (tourActiveRef.current) {
        setTimeout(() => advanceTour(), 300);
      }
    } else {
      setActiveField(resolvedKey);
    }
  }, []);

  // Update DOM displays from current field values
  const updateDisplays = useCallback((elOverride, listOverride) => {
    const el = elOverride || contractRef.current;
    if (!el) return;
    const list = listOverride || fieldListRef.current;
    const f = fieldsRef.current;

    list.forEach(({ fieldKey, baseKey, nodeIndex }) => {
      const node = el.querySelector(`[data-field-index="${nodeIndex}"]`);
      if (!node) return;
      const cfg = FIELD_CONFIG[fieldKey] || FIELD_CONFIG[baseKey] || {};
      const resolvedKey = fieldKey.startsWith('initials_dup_') ? 'initials' : fieldKey;

      if (baseKey === 'date') return; // already handled

      // Checkbox
      if (cfg.type === 'checkbox') {
        const checked = !!f[resolvedKey];
        node.style.border = checked ? '2px solid #16a34a' : `2px solid ${cfg.color}`;
        node.style.backgroundColor = checked ? '#16a34a15' : `${cfg.color}15`;
        node.innerHTML = checked ? '&#10004;' : '&nbsp;';
        node.style.color = checked ? '#16a34a' : '';
        node.style.fontWeight = checked ? 'bold' : '';
        return;
      }

      // Signature
      if (resolvedKey === 'signature') {
        if (signatureImage && signatureType === 'drawn') {
          node.innerHTML = `<img src="${signatureImage}" style="height: 40px;" />`;
        } else if (f.signature) {
          node.innerHTML = `<span style="font-family: 'Brush Script MT', 'Segoe Script', cursive; font-size: 28px;">${f.signature}</span>`;
        } else {
          node.innerHTML = `<span style="color: ${cfg.color}; font-size: 13px; font-weight: 600;">${cfg.label} &#9998;</span>`;
          node.style.borderBottom = `2px dashed ${cfg.color}`;
          node.style.backgroundColor = `${cfg.color}12`;
          return;
        }
        node.style.borderBottom = '2px solid #16a34a';
        node.style.backgroundColor = '#16a34a10';
        return;
      }

      // Text
      const val = f[resolvedKey] || '';
      if (val) {
        node.textContent = val;
        node.style.borderBottom = '2px solid #16a34a';
        node.style.backgroundColor = '#16a34a10';
      } else {
        const label = node.getAttribute('data-label') || cfg.label || 'Fill';
        node.innerHTML = `<span style="color: ${cfg.color || '#999'}; font-size: 13px; font-weight: 600;">${label} &#9998;</span>`;
        node.style.borderBottom = `2px dashed ${cfg.color || '#999'}`;
        node.style.backgroundColor = `${cfg.color || '#999'}12`;
      }
    });
  }, [signatureImage, signatureType]);

  // Re-run displays whenever fields change
  useEffect(() => { updateDisplays(); }, [fields, signatureImage, signatureType, updateDisplays]);

  // Auto-populate initials from name
  useEffect(() => {
    if (fields.printed_name) {
      const initials = fields.printed_name.split(/\s+/).map((w) => w[0]?.toUpperCase() || '').join('');
      setFields((prev) => prev.initials !== initials ? { ...prev, initials } : prev);
    }
  }, [fields.printed_name]);

  // Tour: highlight current field and scroll to it
  useEffect(() => {
    if (!tourActive || tourStep < 0 || !contractRef.current) return;
    const el = contractRef.current;
    const list = fieldList;

    // Clear all highlights
    list.forEach(({ nodeIndex }) => {
      const node = el.querySelector(`[data-field-index="${nodeIndex}"]`);
      if (node) { node.style.animation = ''; node.style.boxShadow = ''; }
    });

    const current = list[tourStep];
    if (!current) return;
    const node = el.querySelector(`[data-field-index="${current.nodeIndex}"]`);
    if (!node) return;

    const cfg = FIELD_CONFIG[current.fieldKey] || FIELD_CONFIG[current.baseKey] || {};
    node.style.animation = 'pulse-field 1.5s ease-in-out infinite';
    node.style.boxShadow = `0 0 0 4px ${cfg.color || '#ff680d'}40`;
    node.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [tourStep, tourActive, fieldList]);

  // Advance tour to next field
  const advanceTour = useCallback(() => {
    const next = tourStepRef.current + 1;
    const list = fieldListRef.current;
    if (next >= list.length) {
      setTourActive(false);
      setTourStep(-1);
      // Clear highlights
      if (contractRef.current) {
        list.forEach(({ nodeIndex }) => {
          const node = contractRef.current.querySelector(`[data-field-index="${nodeIndex}"]`);
          if (node) { node.style.animation = ''; node.style.boxShadow = ''; }
        });
      }
    } else {
      // Skip auto fields (date)
      const entry = list[next];
      const cfg = FIELD_CONFIG[entry?.fieldKey] || FIELD_CONFIG[entry?.baseKey];
      if (cfg?.type === 'auto') {
        setTourStep(next);
        setTimeout(() => advanceTour(), 100);
      } else {
        setTourStep(next);
      }
    }
  }, []);

  const handleTourNext = useCallback(() => {
    if (tourStepRef.current < 0) {
      setTourStep(0);
      return;
    }
    const list = fieldListRef.current;
    const current = list[tourStepRef.current];
    if (!current) return;

    const cfg = FIELD_CONFIG[current.fieldKey] || FIELD_CONFIG[current.baseKey] || {};
    const resolvedKey = current.fieldKey.startsWith('initials_dup_') ? 'initials' : current.fieldKey;

    if (resolvedKey === 'signature') {
      setSigModalOpen(true);
    } else if (cfg.type === 'checkbox') {
      setFields((prev) => {
        if (resolvedKey === 'billing_option_1') return { ...prev, billing_option_1: true, billing_option_2: false };
        if (resolvedKey === 'billing_option_2') return { ...prev, billing_option_2: true, billing_option_1: false };
        return prev;
      });
      setTimeout(() => advanceTour(), 300);
    } else {
      setActiveField(resolvedKey);
    }
  }, [advanceTour]);

  // Field done → save value and advance tour
  const handleFieldDone = useCallback((fieldKey, value) => {
    if (fieldKey) {
      const resolved = fieldKey.startsWith('initials_dup_') ? 'initials' : fieldKey;
      setFields((prev) => ({ ...prev, [resolved]: value ?? prev[resolved] }));
    }
    setActiveField(null);
    if (tourActiveRef.current) advanceTour();
  }, [advanceTour]);

  const handleSigSave = useCallback(() => {
    if (sigModalTab === 0) {
      const typed = sigModalTyped || fieldsRef.current.printed_name;
      setFields((prev) => ({ ...prev, signature: typed }));
      setSignatureType('typed');
      setSignatureImage('');
    } else {
      const canvas = canvasRef.current;
      if (canvas) { setSignatureImage(canvas.toDataURL('image/png')); setSignatureType('drawn'); }
    }
    setSigModalOpen(false);
    if (tourActiveRef.current) advanceTour();
  }, [sigModalTab, sigModalTyped, advanceTour]);

  // Canvas
  const startDraw = useCallback((e) => {
    const c = canvasRef.current; if (!c) return;
    isDrawingRef.current = true;
    const ctx = c.getContext('2d'); const r = c.getBoundingClientRect();
    ctx.beginPath();
    ctx.moveTo((e.clientX || e.touches?.[0]?.clientX) - r.left, (e.clientY || e.touches?.[0]?.clientY) - r.top);
  }, []);
  const draw = useCallback((e) => {
    if (!isDrawingRef.current) return;
    const c = canvasRef.current; if (!c) return;
    const ctx = c.getContext('2d'); const r = c.getBoundingClientRect();
    ctx.lineWidth = 2; ctx.lineCap = 'round'; ctx.strokeStyle = '#111';
    ctx.lineTo((e.clientX || e.touches?.[0]?.clientX) - r.left, (e.clientY || e.touches?.[0]?.clientY) - r.top);
    ctx.stroke();
  }, []);
  const endDraw = useCallback(() => { isDrawingRef.current = false; }, []);
  const clearCanvas = () => { const c = canvasRef.current; if (c) c.getContext('2d').clearRect(0, 0, c.width, c.height); };

  const billingSelected = fields.billing_option_1 || fields.billing_option_2;
  const requiredComplete = fields.printed_name && (fields.signature || signatureImage) && fields.initials && billingSelected;

  const handleSubmit = async () => {
    if (!requiredComplete) { toast.error('Please complete all required fields.'); return; }
    if (!agreed) { toast.error('Please agree to the terms.'); return; }
    setSubmitting(true);
    try {
      await signContract(signToken, {
        signature_data: {
          signature_image: signatureImage,
          signature_type: signatureType,
          typed_name: signatureType === 'typed' ? (fields.signature || fields.printed_name) : '',
          billing_option: fields.billing_option_1 ? 'option_1' : 'option_2',
        },
        printed_name: fields.printed_name.trim(),
        title: fields.title.trim(),
      });
      setSigned(true);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to sign contract.'); }
    finally { setSubmitting(false); }
  };

  // --- Render ---

  if (loading) return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <CircularProgress size={48} />
    </Box>
  );

  if (error && !contract) return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Card sx={{ maxWidth: 480, textAlign: 'center', p: 4 }}>
        <Typography variant="h5" gutterBottom>Unable to Load Contract</Typography>
        <Typography color="text.secondary">{error}</Typography>
      </Card>
    </Box>
  );

  // Read-only view of a signed contract
  if (contract?.read_only) return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
      <Helmet><title>Signed Contract | {config.SITE.title}</title></Helmet>
      <Box sx={{ bgcolor: '#111827', py: 2, px: 3 }}>
        <Container maxWidth="lg">
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <img src={leadGurusLogo} alt="Lead Gurus" style={{ height: 28 }} />
            <Typography variant="body2" sx={{ color: '#9ca3af' }}>{contract.client_name}</Typography>
          </Stack>
        </Container>
      </Box>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Card sx={{ mb: 3, border: '2px solid #16a34a', bgcolor: '#f0fdf4' }}>
          <CardContent sx={{ textAlign: 'center', py: 2.5 }}>
            <Stack direction="row" spacing={1.5} justifyContent="center" alignItems="center">
              <CheckCircleIcon sx={{ color: 'success.main', fontSize: 28 }} />
              <Box>
                <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#16a34a' }}>
                  Contract Signed by {contract.signer_name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {contract.signed_at ? new Date(contract.signed_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : ''}
                </Typography>
              </Box>
            </Stack>
            {contract.pdf_url && (
              <Button
                variant="outlined" size="small" sx={{ mt: 1.5 }}
                href={contract.pdf_url} target="_blank" rel="noopener noreferrer"
              >
                Download Signed PDF
              </Button>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardContent sx={{ p: 4 }}>
            <div dangerouslySetInnerHTML={{ __html: contract.html }} />
          </CardContent>
        </Card>
      </Container>
    </Box>
  );

  if (signed) return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Helmet><title>Contract Signed | {config.SITE.title}</title></Helmet>
      <Card sx={{ maxWidth: 520, textAlign: 'center', p: 4 }}>
        <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
        <Typography variant="h4" gutterBottom>Contract Signed!</Typography>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          Thank you, {fields.printed_name}. Your contract for <strong>{contract?.client_name}</strong> has been signed successfully.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          You'll receive a confirmation email and onboarding instructions shortly.
        </Typography>
      </Card>
    </Box>
  );

  const currentEntry = tourStep >= 0 && fieldList[tourStep] ? fieldList[tourStep] : null;
  const currentCfg = currentEntry ? (FIELD_CONFIG[currentEntry.fieldKey] || FIELD_CONFIG[currentEntry.baseKey]) : null;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
      <Helmet><title>Sign Contract | {config.SITE.title}</title></Helmet>
      <style>{`@keyframes pulse-field { 0%,100%{transform:scale(1)} 50%{transform:scale(1.03)} }`}</style>

      {/* Header */}
      <Box sx={{ bgcolor: '#111827', py: 2, px: 3 }}>
        <Container maxWidth="md">
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <img src={leadGurusLogo} alt="Lead Gurus" style={{ height: 28 }} />
            <Typography variant="body2" sx={{ color: '#9ca3af' }}>{contract?.client_name}</Typography>
          </Stack>
        </Container>
      </Box>

      {/* Sticky progress */}
      <Box sx={{ bgcolor: '#fff', borderBottom: '1px solid #e5e7eb', py: 1.5, px: 3, position: 'sticky', top: 0, zIndex: 10 }}>
        <Container maxWidth="md">
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {[
                { label: 'Name', done: fields.printed_name },
                { label: 'Sign', done: fields.signature || signatureImage },
                { label: 'Billing', done: billingSelected },
                { label: 'Initials', done: fields.initials },
              ].map(({ label, done }) => (
                <Box key={label} sx={{
                  px: 1.5, py: 0.25, borderRadius: 10, fontSize: 11, fontWeight: 600,
                  bgcolor: done ? '#16a34a' : '#e5e7eb', color: done ? '#fff' : '#666',
                }}>
                  {done ? '\u2713 ' : ''}{label}
                </Box>
              ))}
            </Stack>
            <Button
              variant="contained" size="small" onClick={handleSubmit}
              disabled={!requiredComplete || !agreed || submitting}
              startIcon={submitting ? <CircularProgress size={14} /> : <DrawIcon sx={{ fontSize: 16 }} />}
              sx={{ bgcolor: '#16a34a', '&:hover': { bgcolor: '#15803d' } }}
            >
              {submitting ? 'Signing...' : 'Finish & Sign'}
            </Button>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: 3 }}>
        {/* Welcome */}
        {tourActive && tourStep === -1 && (
          <Card sx={{ mb: 3, border: '2px solid #ff680d', bgcolor: '#fff8f3' }}>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <DrawIcon sx={{ fontSize: 48, color: '#ff680d', mb: 2 }} />
              <Typography variant="h5" fontWeight={700} gutterBottom>Ready to sign your contract?</Typography>
              <Typography color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
                We'll walk you through each field that needs your input. It only takes a minute.
              </Typography>
              <Stack direction="row" spacing={2} justifyContent="center">
                <Button variant="contained" size="large" onClick={handleTourNext} endIcon={<NavigateNextIcon />}
                  sx={{ bgcolor: '#ff680d', '&:hover': { bgcolor: '#ea580c' }, px: 4 }}>
                  Start Signing
                </Button>
                <Button variant="outlined" size="large" onClick={() => { setTourActive(false); setTourStep(-1); }}>
                  I'll do it myself
                </Button>
              </Stack>
            </CardContent>
          </Card>
        )}

        {/* Tour step banner */}
        {tourActive && tourStep >= 0 && currentCfg && (
          <Alert severity="info" sx={{ mb: 2, borderLeft: `4px solid ${currentCfg.color}` }}
            action={
              <Button color="inherit" size="small" onClick={handleTourNext} endIcon={<ArrowDownwardIcon sx={{ fontSize: 16 }} />}>
                Fill this field
              </Button>
            }>
            <strong>Step {tourStep + 1} of {fieldList.length}:</strong>{' '}
            {currentCfg.label}{currentCfg.required ? ' (required)' : ' (optional)'}
            {currentCfg.type === 'checkbox' ? ' — click the checkbox or this button' : currentCfg.type === 'signature' ? ' — click to add your signature' : ' — click the highlighted field or this button'}
          </Alert>
        )}

        {/* Contract with delegated click handler */}
        <Card>
          <CardContent sx={{ p: { xs: 2, md: 4 } }}>
            <Box
              ref={contractRef}
              onClick={handleContractClick}
              sx={{
                '& h1, & h2, & h3': { mt: 2, mb: 1 },
                '& p': { mb: 1, lineHeight: 1.7 },
                '& table': { width: '100%', borderCollapse: 'collapse', mb: 2 },
                '& td, & th': { border: '1px solid #ddd', p: 1, fontSize: 13 },
                '& .sig-table td, & .sig-table th': { border: 'none' },
              }}
              dangerouslySetInnerHTML={{ __html: contract?.html || '' }}
            />
          </CardContent>
        </Card>

        {/* Bottom */}
        {!tourActive && (
          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <FormControlLabel
              control={<Checkbox checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />}
              label={<Typography variant="body2">I agree that this electronic signature constitutes my legal signature on this document.</Typography>}
              sx={{ mb: 2 }}
            />
            <br />
            <Button variant="contained" size="large" onClick={handleSubmit}
              disabled={!requiredComplete || !agreed || submitting}
              startIcon={submitting ? <CircularProgress size={18} /> : <DrawIcon />}
              sx={{ bgcolor: '#16a34a', '&:hover': { bgcolor: '#15803d' }, px: 6 }}>
              {submitting ? 'Signing...' : 'Finish & Sign'}
            </Button>
            {!requiredComplete && (
              <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
                Please complete all highlighted fields before signing.
              </Typography>
            )}
          </Box>
        )}

        {!tourActive && requiredComplete && !agreed && (
          <Card sx={{ mt: 3, border: '2px solid #16a34a', bgcolor: '#f0fdf4' }}>
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <CheckCircleIcon sx={{ fontSize: 40, color: '#16a34a', mb: 1 }} />
              <Typography variant="h6" fontWeight={700} gutterBottom>All fields completed!</Typography>
              <Typography color="text.secondary">Check the box above and click "Finish & Sign" to complete.</Typography>
            </CardContent>
          </Card>
        )}
      </Container>

      {/* FAB */}
      {tourActive && tourStep >= 0 && currentCfg && (
        <Fab variant="extended" onClick={handleTourNext}
          sx={{
            position: 'fixed', bottom: 24, right: 24, zIndex: 20, px: 3,
            bgcolor: currentCfg.color, color: '#fff',
            '&:hover': { bgcolor: currentCfg.color, filter: 'brightness(0.9)' },
          }}>
          <ArrowDownwardIcon sx={{ mr: 1 }} />
          {currentCfg.label}
        </Fab>
      )}

      {/* Text field modal */}
      <Dialog open={!!activeField} onClose={() => handleFieldDone(activeField, activeField ? fields[activeField.startsWith('initials_dup_') ? 'initials' : activeField] : '')}
        maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            {activeField && (FIELD_CONFIG[activeField]?.label || 'Fill in')}
          </Typography>
          <TextField
            autoFocus fullWidth
            value={activeField ? fields[activeField] || '' : ''}
            onChange={(e) => { const f = activeField; setFields((prev) => ({ ...prev, [f]: e.target.value })); }}
            onKeyDown={(e) => { if (e.key === 'Enter') handleFieldDone(activeField, fields[activeField]); }}
            placeholder={activeField && (FIELD_CONFIG[activeField]?.label || '')}
            sx={{ mt: 1 }}
          />
          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button onClick={() => handleFieldDone(activeField, fields[activeField])} variant="contained" size="small">
              {tourActive ? 'Next' : 'Done'}
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>

      {/* Signature modal */}
      <Dialog open={sigModalOpen} onClose={() => { setSigModalOpen(false); if (tourActiveRef.current) advanceTour(); }}
        maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Add Your Signature</Typography>
          <Tabs value={sigModalTab} onChange={(_, v) => setSigModalTab(v)} sx={{ mb: 2 }}>
            <Tab label="Type" /><Tab label="Draw" />
          </Tabs>
          {sigModalTab === 0 && (
            <Box>
              <TextField autoFocus fullWidth label="Type your name" value={sigModalTyped}
                onChange={(e) => setSigModalTyped(e.target.value)}
                placeholder={fields.printed_name || 'Your name'} sx={{ mb: 2 }} />
              <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 3, textAlign: 'center', bgcolor: '#fafafa', minHeight: 80, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography sx={{ fontFamily: "'Brush Script MT', 'Segoe Script', cursive", fontSize: 36, color: '#111' }}>
                  {sigModalTyped || fields.printed_name || 'Your signature'}
                </Typography>
              </Box>
            </Box>
          )}
          {sigModalTab === 1 && (
            <Box>
              <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, bgcolor: '#fff', mb: 1 }}>
                <canvas ref={canvasRef} width={460} height={150}
                  style={{ display: 'block', width: '100%', cursor: 'crosshair' }}
                  onMouseDown={startDraw} onMouseMove={draw} onMouseUp={endDraw} onMouseLeave={endDraw}
                  onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={endDraw} />
              </Box>
              <Button size="small" onClick={clearCanvas}>Clear</Button>
            </Box>
          )}
          <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 2 }}>
            <Button onClick={() => { setSigModalOpen(false); if (tourActiveRef.current) advanceTour(); }}>Cancel</Button>
            <Button variant="contained" onClick={handleSigSave}>
              {tourActive ? 'Apply & Next' : 'Apply Signature'}
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
